import { useMemo, useState } from 'react';
import { api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { useCloud } from '../context/CloudContext';
import PageHeader from '../components/PageHeader';
import Card from '../components/Card';
import StatusBadge from '../components/StatusBadge';
import ProviderAvatar from '../components/ProviderAvatar';
import ProvisionDialog from '../components/ProvisionDialog';
import { Loading, ErrorState, EmptyState } from '../components/States';
import { currency } from '../lib/format';

const CATEGORIES = [
  'Compute',
  'Storage',
  'Database',
  'Networking',
  'Security',
  'Analytics',
];
const STATUSES = ['running', 'provisioning', 'stopped', 'error'];

export default function Resources() {
  const { providers, activeProvider } = useCloud();
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [query, setQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const params = useMemo(
    () => ({ providerId: activeProvider, category, status, q: query }),
    [activeProvider, category, status, query]
  );

  const { data, loading, error, reload } = useAsync(
    () => api.getResources(params),
    [params]
  );

  const providerById = useMemo(
    () => Object.fromEntries(providers.map((p) => [p.id, p])),
    [providers]
  );

  const handleDelete = async (id) => {
    setBusyId(id);
    try {
      await api.deleteResource(id);
      reload();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="stack">
      <PageHeader
        title="Resources"
        subtitle="Inventory across every connected cloud."
        actions={
          <button className="btn btn--primary" onClick={() => setDialogOpen(true)}>
            + Provision resource
          </button>
        }
      />

      <Card
        title="Filters"
        action={
          <input
            className="input search"
            placeholder="Search name or type…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        }
      >
        <div className="filter-row">
          <label className="field">
            <span>Category</span>
            <select
              className="input"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="">All categories</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Status</span>
            <select
              className="input"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">All statuses</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
        </div>
      </Card>

      <Card title={`Resources ${data ? `(${data.length})` : ''}`}>
        {loading ? (
          <Loading />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : data.length === 0 ? (
          <EmptyState
            title="No resources match"
            detail="Try clearing filters or provision a new resource."
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Provider</th>
                  <th>Type</th>
                  <th>Region</th>
                  <th>Status</th>
                  <th className="num">Monthly cost</th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {data.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <div className="cell-primary">{r.name}</div>
                      <div className="cell-sub mono">{r.spec}</div>
                    </td>
                    <td>
                      <div className="cell-provider">
                        <ProviderAvatar
                          provider={providerById[r.providerId]}
                          size={24}
                        />
                        {providerById[r.providerId]?.shortName || r.providerId}
                      </div>
                    </td>
                    <td>
                      <span className="cell-type">{r.type}</span>
                      <div className="cell-sub">{r.category}</div>
                    </td>
                    <td>
                      <code className="mono">{r.region}</code>
                    </td>
                    <td>
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="num">{currency(r.monthlyCost)}</td>
                    <td className="num">
                      <button
                        className="btn btn--danger-ghost btn--sm"
                        onClick={() => handleDelete(r.id)}
                        disabled={busyId === r.id}
                      >
                        {busyId === r.id ? '…' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {dialogOpen && (
        <ProvisionDialog
          providers={providers}
          onClose={() => setDialogOpen(false)}
          onCreated={() => {
            setDialogOpen(false);
            reload();
          }}
        />
      )}
    </div>
  );
}
